import type { ContactCohortFilter } from "../types";

/** Star toggle order in the composer (5★ first, most-used segment on top). */
export const RATING_STAR_OPTIONS = [5, 4, 3, 2, 1] as const;

const CONTACT_RATING_FILTER_KEY = "last_rating@in";

export function normalizeSelectedRatings(stars: readonly number[]): number[] {
  return [...new Set(stars)]
    .filter((star) => Number.isInteger(star) && star >= 1 && star <= 5)
    .sort((a, b) => a - b);
}

/**
 * List filter narrowing the audience selector's on-screen contact list to the
 * chosen stars. The list grammar cannot express "unrated" (no null operator),
 * so when Unrated is included the list stays unfiltered — the authoritative
 * audience numbers always come from the server-side preview.
 */
export function buildContactRatingListFilter(
  selectedStars: readonly number[],
  includeUnrated: boolean,
): Record<string, unknown> {
  const stars = normalizeSelectedRatings(selectedStars);
  if (!stars.length || includeUnrated) {
    return {};
  }
  return { [CONTACT_RATING_FILTER_KEY]: stars.join(",") };
}

/**
 * Folds the rating refinement into a cohort filter the same way the geography
 * toggles fold in: set when active, deleted when cleared (a stale key would
 * silently keep narrowing the audience).
 */
export function applyRatingToCohortFilter(
  filter: ContactCohortFilter,
  selectedStars: readonly number[],
  includeUnrated: boolean,
): ContactCohortFilter {
  const next = { ...filter };
  const stars = normalizeSelectedRatings(selectedStars);
  if (stars.length) {
    next.last_rating_any = stars;
  } else {
    delete next.last_rating_any;
  }
  if (includeUnrated) {
    next.last_rating_none = true;
  } else {
    delete next.last_rating_none;
  }
  return next;
}
